import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login';
import ChangePassword from './components/ChangePassword';
import DashboardRouter from './components/dashboards/DashboardRouter';
import Dashboard from './components/Dashboard';
import EnhancedMakerDashboard from './components/dashboards/EnhancedMakerDashboard';
import EnhancedCheckerDashboard from './components/dashboards/EnhancedCheckerDashboard';
import EmployeeDashboard from './components/dashboards/EmployeeDashboard';
import PensionerDashboard from './components/dashboards/PensionerDashboard';
import Employee360Profile from './components/admin/Employee360Profile';
import PensionerProfilePage from './components/admin/PensionerProfilePage';
import EmployeeFormOverview from './components/employee/Forms/EmployeeFormOverview';
import FormRenderer from './components/employee/SystemForms/FormRenderer';
import PreviewRenderer from './components/employee/SystemForms/PreviewRenderer';
import PFInterest from './components/providentfund/PFInterest';
import './App.css';

function RootRedirect() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
}

function NotFoundRedirect() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
}

function App() {
  return (
    <Router>
      <NotificationProvider>
        <AuthProvider>
          <div className="App">
          <Routes>
            {/* Root route: smart redirect based on authentication */}
            <Route path="/" element={<RootRedirect />} />

            <Route path="/login" element={<Login />} />

            <Route 
              path="/change-password" 
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              } 
            />
            
            {/* Protected Routes - Auth Required */}
            <Route path="/dashboard" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />
            <Route path="/hrms" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />
            <Route path="/admin-dashboard" element={<ProtectedRoute requiredDashboardTier="admin"><Dashboard /></ProtectedRoute>} />
            <Route path="/checker-dashboard" element={<ProtectedRoute requiredDashboardTier="checker"><EnhancedCheckerDashboard /></ProtectedRoute>} />
            <Route path="/maker-dashboard" element={<ProtectedRoute requiredDashboardTier="maker"><EnhancedMakerDashboard /></ProtectedRoute>} />
            <Route path="/user-dashboard" element={<ProtectedRoute requiredDashboardTier="user"><EmployeeDashboard /></ProtectedRoute>} />
            <Route path="/pensioner-dashboard" element={<ProtectedRoute requiredRole={['pensioner']}><PensionerDashboard /></ProtectedRoute>} />
            <Route path="/pensioners/employee/:employeeId" element={<ProtectedRoute requiredPermission="pensioner:view:all"><PensionerProfilePage /></ProtectedRoute>} />

            <Route
              path="/employees/:employeeId/profile"
              element={
                <ProtectedRoute>
                  <Employee360Profile />
                </ProtectedRoute>
              }
            />

            {/* Employee Form Routes */}
            <Route
              path="/employee/forms"
              element={
                <ProtectedRoute>
                  <Navigate to="/user-dashboard?tab=my-forms" replace />
                </ProtectedRoute>
              }
            />

            <Route
              path="/employee/forms/:formCode"
              element={
                <ProtectedRoute>
                  <EmployeeFormOverview />
                </ProtectedRoute>
              }
            />

            <Route
              path="/employee/forms/fill/:formCode"
              element={
                <ProtectedRoute>
                  <FormRenderer />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/employees/:employeeId/forms/fill/:formCode"
              element={
                <ProtectedRoute>
                  <FormRenderer />
                </ProtectedRoute>
              }
            />

            <Route
              path="/employee/forms/:formCode/preview"
              element={
                <ProtectedRoute>
                  <PreviewRenderer />
                </ProtectedRoute>
              }
            />

            <Route
              path="/pf-interest"
              element={
                <ProtectedRoute>
                  <PFInterest />
                </ProtectedRoute>
              }
            />

            {/* Legacy admin dashboard route */}
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute requiredDashboardTier="admin">
                  <Dashboard />
                </ProtectedRoute>
              } 
            />

            {/* Module route aliases linking directly to their dashboard tab */}
            <Route path="/admin/employees" element={<ProtectedRoute requiredDashboardTier={['admin', 'maker', 'checker']}><Navigate to="/admin-dashboard?tab=employee-management" replace /></ProtectedRoute>} />
            <Route path="/admin/payroll" element={<ProtectedRoute requiredDashboardTier={['admin', 'maker', 'checker']}><Navigate to="/admin-dashboard?tab=payroll" replace /></ProtectedRoute>} />
            <Route path="/admin/attendance" element={<ProtectedRoute requiredDashboardTier={['admin', 'maker', 'checker']}><Navigate to="/admin-dashboard?tab=attendance" replace /></ProtectedRoute>} />
            <Route path="/admin/leaves" element={<ProtectedRoute><Navigate to="/admin-dashboard?tab=leave" replace /></ProtectedRoute>} />
            <Route path="/admin/reports" element={<ProtectedRoute requiredDashboardTier={['admin', 'maker', 'checker']}><Navigate to="/admin-dashboard?tab=reports" replace /></ProtectedRoute>} />
            <Route path="/admin/pension-payroll" element={<ProtectedRoute requiredDashboardTier="admin"><Navigate to="/admin-dashboard?tab=pension-payroll" replace /></ProtectedRoute>} />

            <Route path="/employee/profile" element={<ProtectedRoute><Navigate to="/user-dashboard?tab=my-profile" replace /></ProtectedRoute>} />
            <Route path="/employee/attendance" element={<ProtectedRoute><Navigate to="/user-dashboard?tab=attendance" replace /></ProtectedRoute>} />
            <Route path="/employee/leaves" element={<ProtectedRoute><Navigate to="/user-dashboard?tab=leaves" replace /></ProtectedRoute>} />
            <Route path="/employee/salary-slips" element={<ProtectedRoute><Navigate to="/user-dashboard?tab=salary-slips" replace /></ProtectedRoute>} />
            <Route path="/employee/loans" element={<ProtectedRoute><Navigate to="/user-dashboard?tab=my-loans" replace /></ProtectedRoute>} />
            <Route path="/employee/advances" element={<ProtectedRoute><Navigate to="/user-dashboard?tab=my-advances" replace /></ProtectedRoute>} />
            <Route path="/employee/festival-advance" element={<ProtectedRoute><Navigate to="/user-dashboard?tab=festival-advance" replace /></ProtectedRoute>} />
            <Route path="/employee/pf" element={<ProtectedRoute><Navigate to="/user-dashboard?tab=my-pf" replace /></ProtectedRoute>} />
            <Route path="/employee/bank-loans" element={<ProtectedRoute><Navigate to="/user-dashboard?tab=bank-loans" replace /></ProtectedRoute>} />
            <Route path="/employee/lic" element={<ProtectedRoute><Navigate to="/user-dashboard?tab=my-lic" replace /></ProtectedRoute>} />
            <Route path="/employee/reimbursement" element={<ProtectedRoute><Navigate to="/user-dashboard?tab=reimbursement" replace /></ProtectedRoute>} />
            <Route path="/employee/income-tax" element={<ProtectedRoute><Navigate to="/user-dashboard?tab=income-tax" replace /></ProtectedRoute>} />
            <Route path="/employee/increment" element={<ProtectedRoute><Navigate to="/user-dashboard?tab=my-increment" replace /></ProtectedRoute>} />

            {/* Unknown URL fallback */}
            <Route path="*" element={<NotFoundRedirect />} />
          </Routes>
        </div>
      </AuthProvider>
    </NotificationProvider>
  </Router>
);
}

export default App;
